// Live dashboard-build preview — rendered inline in the chat while the
// user is conversationally building a dashboard with the agent.
//
// The draft lives ONLY in frontend React state (AiChat.jsx's
// dashboardDraft state, fed by the "dashboard_draft_update" WS event)
// until the user clicks "Save Dashboard" here — nothing is persisted to
// DynamoDB until that explicit click (locked decision, ai-chat-dashboard-
// tracker.md "Why not raw HTML": "A page refresh mid-build loses the
// unsaved draft — treated as an acceptable builder-UX tradeoff, avoids
// littering the database with half-finished dashboards nobody completes").
// Same WidgetRenderer as the saved dashboard page — one rendering path.

import React, { useState } from 'react';
import { Button, Alert, Box } from '@cloudscape-design/components';
import WidgetRenderer from './WidgetRenderer.jsx';
import { dashboardsApi } from './dashboardsApi.js';

export default function DashboardDraftPreview({ draft, sessionId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [editSaved, setEditSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (draft.is_edit && draft.dashboard_id) {
        // Editing an existing dashboard — PATCH (update), not POST (create)
        const widgetsForSave = draft.widgets.map((w) => ({
          widget_id: w.widget_id, title: w.title, sql: w.sql,
          viz_type: w.viz_type, viz_config: w.viz_config || {},
        }));
        await dashboardsApi.updateDashboard(draft.dashboard_id, {
          title: draft.title, widgets: widgetsForSave,
        });
        setEditSaved(true);
        onSaved?.();
      } else {
        // New dashboard — POST (create)
        const widgetsForSave = draft.widgets.map((w) => ({
          widget_id: w.widget_id, title: w.title, sql: w.sql,
          viz_type: w.viz_type, viz_config: w.viz_config || {},
        }));
        const saved = await dashboardsApi.createDashboard({
          title: draft.title, widgets: widgetsForSave,
          source_session_id: sessionId,
        });
        setSavedId(saved.dashboard_id);
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const okCount = draft.widgets.filter((w) => w.ok).length;

  return (
    <div className="ai-chat-dashboard-draft">
      <div className="ai-chat-dashboard-draft-header">
        <span className="ai-chat-dashboard-draft-title">📊 {draft.title}</span>
        <span className="ai-chat-dashboard-draft-badge">
          {draft.widgets.length} widget{draft.widgets.length === 1 ? '' : 's'}
          {okCount < draft.widgets.length && ` · ${draft.widgets.length - okCount} error(s)`}
        </span>
      </div>

      <div className="ai-chat-widget-grid ai-chat-widget-grid-compact">
        {draft.widgets.map((w) => <WidgetRenderer key={w.widget_id} widget={w} />)}
      </div>

      {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

      {editSaved ? (
        <Alert type="success">
          ✅ Dashboard updated.{' '}
          <a href={`/ai-chat/dashboard/${draft.dashboard_id}`} target="_blank" rel="noreferrer">Open dashboard</a>
        </Alert>
      ) : savedId ? (
        <Alert type="success">
          Saved! Find it under <strong>Your Dashboards</strong> in the sidebar, or{' '}
          <a href={`/ai-chat/dashboard/${savedId}`} target="_blank" rel="noreferrer">open it in a new tab</a>.
        </Alert>
      ) : (
        <Box padding={{ top: 's' }}>
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={draft.widgets.length === 0}>
            {draft.is_edit ? 'Save changes' : 'Save Dashboard'}
          </Button>
        </Box>
      )}
    </div>
  );
}
